// Leaderboard client — thin fetch wrappers over /api/scores (a
// Cloudflare Pages Function that rides along with the static deploy).
// Every call degrades gracefully: when the API is missing (vite dev
// server, or the D1 binding isn't configured yet) callers get
// { ok: false } and the UI shows an "unavailable" state instead of
// throwing. Bot protection happens at the edge (Zero Trust / WAF
// managed challenge on the zone), not in this client.

import { authHeader } from './auth';
import { formatTime } from '../modes/modes';
import type { ModeId } from '../types';

/** Modes that accept submissions, with the metric each one ranks by.
 *  Zen is intentionally absent. Time modes rank ascending (faster is
 *  better); score modes rank descending. */
export const LEADERBOARD_MODES: ReadonlyArray<{ id: ModeId; label: string; metric: 'score' | 'time' }> = [
  { id: 'marathon',  label: 'marathon', metric: 'score' },
  { id: 'sprint40',  label: 'sprint',   metric: 'time' },
  { id: 'ultra2min', label: 'ultra',    metric: 'score' },
  { id: 'cheese',    label: 'garbage',  metric: 'time' },
  { id: 'master',    label: 'master',   metric: 'score' },
];

export function leaderboardMetric(mode: ModeId): 'score' | 'time' | null {
  return LEADERBOARD_MODES.find((m) => m.id === mode)?.metric ?? null;
}

/** Format a stored leaderboard value for display, mode-relative:
 *  elapsed time for sprint/garbage, locale-formatted points otherwise. */
export function formatLeaderboardValue(mode: ModeId, value: number): string {
  return leaderboardMetric(mode) === 'time' ? formatTime(value) : Math.round(value).toLocaleString();
}

export interface LeaderboardEntry { rank: number; name: string; value: number; when: number }
export interface LeaderboardPage {
  ok: boolean;
  error?: string;
  entries: LeaderboardEntry[];
  total: number;
  you?: { rank: number; name: string; value: number };
}
export interface SubmitResult {
  ok: boolean;
  error?: string;
  rank?: number;
  total?: number;
  improved?: boolean;
  best?: number;
}

export async function fetchLeaderboard(mode: ModeId): Promise<LeaderboardPage> {
  try {
    const qs = new URLSearchParams({ mode });
    // Authorization (when signed in) lets the server include 'you'.
    const res = await fetch(`/api/scores?${qs.toString()}`, { headers: { accept: 'application/json', ...authHeader() } });
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { error?: string } | null;
      return { ok: false, error: body?.error ?? `http ${res.status}`, entries: [], total: 0 };
    }
    return await res.json() as LeaderboardPage;
  } catch {
    return { ok: false, error: 'network error', entries: [], total: 0 };
  }
}

export async function submitScore(args: { mode: ModeId; value: number }): Promise<SubmitResult> {
  try {
    // Identity comes entirely from the session — the server derives
    // name + key from the account, so nothing spoofable rides the body.
    const res = await fetch('/api/scores', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeader() },
      body: JSON.stringify(args),
    });
    const body = await res.json().catch(() => null) as SubmitResult | null;
    if (!res.ok || !body) return { ok: false, error: body?.error ?? `http ${res.status}` };
    return body;
  } catch {
    return { ok: false, error: 'network error' };
  }
}
